import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    output,
    signal,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DxLoadIndicatorModule } from 'devextreme-angular/ui/load-indicator';
import { catchError, map, of } from 'rxjs';
import { BlockableEntityType, IEntityBlockRecord } from 'src/app/features/entity-blocks/models/entity-block.models';
import { EntityBlockService } from 'src/app/features/entity-blocks/services/entity-block.service';
import { IRequiredAction, RequiredActionOwnerType } from 'src/app/features/required-actions/models/required-action.models';
import { RequiredActionsService } from 'src/app/features/required-actions/services/required-actions.service';
import { getStatusIconClass, getStatusIndicatorClass } from './status-badge.utils';

const BLOCKED_STATUS = 'Blocked';
const ACTION_REQUIRED_STATUS = 'ActionRequired';
const DELETED_STATUS = 'Deleted';
const TOOLTIP_GAP_PX = 6;

interface IBlockTooltipRow {
    reasonLabel: string;
    description: string | null;
}

/** `entityType`/`entityId` identify the same real-world entity for both the block and required-action APIs. */
const BLOCKABLE_TO_OWNER_TYPE: Record<BlockableEntityType, RequiredActionOwnerType> = {
    [BlockableEntityType.Customer]: RequiredActionOwnerType.Customer,
    [BlockableEntityType.CustomerOrder]: RequiredActionOwnerType.CustomerOrder,
    [BlockableEntityType.ProductionOrder]: RequiredActionOwnerType.ProductionOrder,
};

@Component({
    selector: 'app-status-badge',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [TranslocoPipe, DxLoadIndicatorModule],
    templateUrl: './status-badge.component.html',
    styleUrl: './status-badge.component.scss',
    host: {
        '[class]': '"badge status app-status-badge " + hostClasses() + iconOnly()',
        '[style.height.px]': 'height()',
        '[style.min-height.px]': 'height()',
        '[style.cursor]': 'clickHintTranslationKey() ? "pointer" : "default"',
        '(click)': 'badgeClick.emit($event)',
        '(mouseenter)': 'onMouseEnter()',
        '(mouseleave)': 'isTooltipVisible.set(false)',
    },
})
export class StatusBadgeComponent {
    private readonly entityBlockService = inject(EntityBlockService);
    private readonly requiredActionsService = inject(RequiredActionsService);
    private readonly translocoService = inject(TranslocoService);
    private readonly destroyRef = inject(DestroyRef);

    /** Domain enum value (e.g. `Draft`, `InProgress`, `Canceled`). */
    readonly status = input.required<string | null | undefined>();

    /** Plain label; overrides transloco when set. */
    readonly label = input<string | null>(null);

    /** Full transloco key for the label. */
    readonly translationKey = input<string | null>(null);

    /** Builds `translationKey` as `{prefix}{status}` when `translationKey` is not set. */
    readonly translationPrefix = input<string | null>(null);

    readonly showIcon = input(true);
    readonly showText = input(true);

    /** Overrides the default icon for this status. */
    readonly icon = input<string | null>(null);

    /** Plain hover tooltip text; overrides `tooltipTranslationKey` and the label fallback when set. */
    readonly tooltip = input<string | null>(null);

    /** Full transloco key for the hover tooltip; falls back to the badge label when not set. */
    readonly tooltipTranslationKey = input<string | null>(null);

    /** Hover tooltip anchor: the badge host element, so the whole badge is hoverable. */
    protected readonly tooltipTarget = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

    /**
     * Not `dx-tooltip`: inside a dx-toolbar item, DevExtreme's overlay never completes its show sequence —
     * confirmed across showEvent/hideEvent, a direct `[visible]` binding, and calling `.show()` on the widget
     * instance directly. In every case `_renderContent()`'s Angular template integration callback never
     * resolves, so `onShowing` never fires and the returned promise never settles; no error, just a permanent
     * hang. A plain element driven by our own signal sidesteps that entirely and works in every context.
     */
    protected readonly isTooltipVisible = signal(false);

    protected readonly tooltipTop = signal(0);
    protected readonly tooltipLeft = signal(0);

    private readonly tooltipEl = viewChild<ElementRef<HTMLElement>>('tooltipEl');
    private tooltipMovedToBody = false;

    constructor() {
        /**
         * Grid cells/rows clip `position: absolute` descendants via `overflow: hidden`, so a tooltip anchored
         * to the badge gets cut off there. Moving the element to `document.body` once, combined with
         * `position: fixed` + viewport coordinates computed on each hover, keeps it unclipped everywhere.
         */
        effect(() => {
            const el = this.tooltipEl()?.nativeElement;
            if (el && !this.tooltipMovedToBody) {
                document.body.appendChild(el);
                this.tooltipMovedToBody = true;
            }
        });

        this.destroyRef.onDestroy(() => this.tooltipEl()?.nativeElement.remove());
    }

    /** Entity id used to fetch the block/required-action reason from the API when `status` is `Blocked` or `ActionRequired`. */
    readonly entityId = input<string | null>(null);

    /** Entity type used to fetch the block/required-action reason from the API when `status` is `Blocked` or `ActionRequired`. */
    readonly entityType = input<BlockableEntityType | null>(null);

    /** Emits on click of the badge; callers decide what clicking should do. */
    readonly badgeClick = output<MouseEvent>();

    /** Set by callers that actually handle `badgeClick`, so the tooltip can hint that clicking shows more info. */
    readonly clickable = input(false);

    /** Icon size in px; falls back to the CSS default when not set. */
    readonly iconSize = input<number | null>(null);

    /** Badge height in px; overrides the default `min-height` when set. */
    readonly height = input<number | null>(null);

    readonly iconClass = computed(() => {
        if (!this.showIcon()) {
            return null;
        }

        return getStatusIconClass(this.status(), this.icon());
    });

    readonly hostClasses = computed(() => getStatusIndicatorClass(this.status()));

    readonly iconOnly = computed(() => {
        if (this.showIcon() && !this.showText()) {
            return ' icon-only';
        }

        return '';
    });

    readonly resolvedTranslationKey = computed(() => {
        if (this.translationKey()) {
            return this.translationKey();
        }

        const prefix = this.translationPrefix();
        const status = this.status();

        if (prefix && status) {
            return `${prefix}${status}`;
        }

        return null;
    });

    /**
     * Only render the tooltip when it would show something beyond what's already on the visible badge label —
     * an explicit tooltip/tooltipTranslationKey, an API-fetched entity detail, or the click hint.
     */
    readonly hasTooltip = computed(
        () => !!(this.tooltip() || this.tooltipTranslationKey() || this.canLoadEntityDetails() || this.clickHintTranslationKey())
    );

    /** `Blocked`/`ActionRequired` badges can fetch their real reason from the API, given an entity to look it up for. */
    private readonly canLoadEntityDetails = computed(
        () => (this.status() === BLOCKED_STATUS || this.status() === ACTION_REQUIRED_STATUS) && !!this.entityId() && !!this.entityType()
    );

    /** Only hint at clicking when the caller actually handles `badgeClick` for this status. `Deleted` only counts for `Customer`, which is the only entity type where clicking it does something. */
    protected readonly clickHintTranslationKey = computed(() => {
        if (!this.clickable()) {
            return null;
        }

        const status = this.status();
        if (status === BLOCKED_STATUS || status === ACTION_REQUIRED_STATUS) {
            return 'clickBadgeForMoreInfo';
        }

        if (status === DELETED_STATUS && this.entityType() === BlockableEntityType.Customer) {
            return 'clickBadgeToRestore';
        }

        return null;
    });

    private readonly entityDetailState = signal<'idle' | 'loading' | 'done'>('idle');
    private readonly entityDetailCacheKey = signal<string | null>(null);

    /** API-fetched required-action reason; null while idle/loading/failed, so the template falls back to the static tooltip. */
    protected readonly entityDetailTooltipText = signal<string | null>(null);

    /** API-fetched block reasons for `Blocked`, rendered as a table; null while idle/loading/failed, so the template falls back to the static tooltip. */
    protected readonly entityDetailBlockRows = signal<IBlockTooltipRow[] | null>(null);

    protected readonly isLoadingEntityDetailTooltip = computed(() => this.canLoadEntityDetails() && this.entityDetailState() === 'loading');

    /** Positions and shows the tooltip, then lazily fetches the entity's real reason — cached per entity so grids don't fire a request per row on render. */
    protected onMouseEnter(): void {
        const rect = this.tooltipTarget.getBoundingClientRect();
        this.tooltipTop.set(rect.bottom + TOOLTIP_GAP_PX);
        this.tooltipLeft.set(rect.left);
        this.isTooltipVisible.set(true);

        if (!this.canLoadEntityDetails()) {
            return;
        }

        const status = this.status();
        const entityType = this.entityType()!;
        const entityId = this.entityId()!;
        const cacheKey = `${status}:${entityType}:${entityId}`;

        if (this.entityDetailState() === 'loading' || (this.entityDetailState() === 'done' && this.entityDetailCacheKey() === cacheKey)) {
            return;
        }

        this.entityDetailState.set('loading');

        if (status === BLOCKED_STATUS) {
            this.entityBlockService
                .getEntityBlocks(entityType, entityId)
                .pipe(
                    map(blocks => this.buildBlockTooltipRows(blocks)),
                    catchError(() => of(null)),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(rows => {
                    this.entityDetailTooltipText.set(null);
                    this.entityDetailBlockRows.set(rows);
                    this.entityDetailCacheKey.set(cacheKey);
                    this.entityDetailState.set('done');
                });
            return;
        }

        this.requiredActionsService
            .getOpenRequiredActionsForOwner(BLOCKABLE_TO_OWNER_TYPE[entityType], entityId)
            .pipe(
                map(actions => this.buildActionRequiredTooltipText(actions)),
                catchError(() => of(null)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(text => {
                this.entityDetailBlockRows.set(null);
                this.entityDetailTooltipText.set(text);
                this.entityDetailCacheKey.set(cacheKey);
                this.entityDetailState.set('done');
            });
    }

    /** Null (falls back to the static tooltip) when there's nothing to show, so API errors/empty results degrade gracefully. */
    private buildBlockTooltipRows(blocks: IEntityBlockRecord[]): IBlockTooltipRow[] | null {
        if (blocks.length === 0) {
            return null;
        }

        return blocks.map(block => ({
            reasonLabel: this.translocoService.translate(`enum_block_reason_${block.reason}`),
            description: block.description?.trim() || null,
        }));
    }

    /** Null (falls back to the static tooltip) when there's nothing to show, so API errors/empty results degrade gracefully. */
    private buildActionRequiredTooltipText(actions: IRequiredAction[]): string | null {
        if (actions.length === 0) {
            return null;
        }

        return actions.map(action => this.actionRequiredReasonLabel(action)).join('\n');
    }

    private actionRequiredReasonLabel(action: IRequiredAction): string {
        const comment = action.comment?.trim();
        if (comment) {
            return comment;
        }

        if (!action.actionKey) {
            return this.translocoService.translate('notSet');
        }

        const reasonKey = `enum_required_action_key_reason_${action.actionKey}`;
        const translatedReason = this.translocoService.translate(reasonKey);
        if (translatedReason !== reasonKey) {
            return translatedReason;
        }

        const labelKey = `enum_required_action_key_${action.actionKey}`;
        const translatedLabel = this.translocoService.translate(labelKey);
        return translatedLabel === labelKey ? action.actionKey : translatedLabel;
    }
}
